
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Input,
    Button,
    Typography,
    Row,
    Col,
    List,
    Space,
    Divider,
    Modal,
    Segmented,
    App
} from 'antd';
import {
    ClearOutlined,
    PlusOutlined,
    MinusOutlined
} from '@ant-design/icons';
import { usePageTitle } from '../context/PageTitleContext';
import { searchProducts, findProductByBarcode } from '../utils/productsSearch';
import SalePaymentModal from "../components/Sale/SalePaymentModal";
import NumbersKeypad from "../components/Sale/NumbersKeypad";
import QuickProducts from "../components/Sale/QuickProducts";
import {deviceCashierRoleService, deviceSalesService} from "../services/posApi";
import {IconUserFilled} from "../../../components/icons";
import {parseMarkingCode, isMarkingCode, validateMarkingForProduct} from '../utils/markingCodeParser';
import MarkingCodeModal from "../components/Sale/MarkingCodeModal";
import AgeVerificationModal from "../components/Sale/AgeVerificationModal";

const { Title, Text } = Typography;

const SalesModePage = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const { message: messageApi } = App.useApp();

    // Состояние корзины
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [barcodeQuery, setBarcodeQuery] = useState('');
    const [barcodeQueue, setBarcodeQueue] = useState([]);
    const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);

    // Состояние модального окна
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCartItem, setSelectedCartItem] = useState(null);
    const [modalMode, setModalMode] = useState('quantity'); // 'quantity' или 'price'
    const [modalValue, setModalValue] = useState('');
    const [isBarcodeListenerActive, setIsBarcodeListenerActive] = useState(true);
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystroke, setLastKeystroke] = useState(0);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [customer, setCustomer] = useState(null);

    const [payCard, setPayCard] = useState(false);

    const [markingModalVisible, setMarkingModalVisible] = useState(false);
    const [pendingMarkingProduct, setPendingMarkingProduct] = useState(null);

    const [duplicateMarkingModalVisible, setDuplicateMarkingModalVisible] = useState(false);
    const [duplicateMarkingCode, setDuplicateMarkingCode] = useState('');

    const [missingMarkingsCount, setMissingMarkingsCount] = useState(0);

    //модальное окно товар не найден
    const [productNotFoundModalVisible, setProductNotFoundModalVisible] = useState(false);
    const [productNotFoundBarcode, setProductNotFoundBarcode] = useState('');

    // Модальное окно баллов лояльности
    // const [loyaltyPointsModalVisible, setLoyaltyPointsModalVisible] = useState(false);
    // const [loyaltyPointsTimer, setLoyaltyPointsTimer] = useState(4);

    // Проверка возраста для товаров 18+
    const [ageVerified, setAgeVerified] = useState(false);
    const [ageVerificationModalVisible, setAgeVerificationModalVisible] = useState(false);
    const [pendingAdultProduct, setPendingAdultProduct] = useState(null);
    const [pendingAdultMarkCode, setPendingAdultMarkCode] = useState(null);


    // Обработчик оплаты (заглушка для будущего функционала)
    const handlePayment = () => {
        if (cart.length === 0) {
            messageApi.warning('Корзина пуста');
            return;
        }

        // Отключаем перехват штрихкодов при переходе к оплате
        setIsBarcodeListenerActive(false);

        // Если клиент уже добавлен, сразу переходим к оплате
        // if (customer) {
            setPaymentModalVisible(true);
        // } else {
        //     // Иначе спрашиваем про баллы
        //     setLoyaltyPointsTimer(4);
        //     setLoyaltyPointsModalVisible(true);
        // }
    };

    // Очистка корзины
    const clearCart = useCallback(() => {
        setCart([]);
        setCustomer(null);
        setAgeVerified(false);
        localStorage.removeItem('sales_cart');
    }, []);

    // Обработчик завершения оплаты
    const handlePaymentComplete = useCallback((paymentData) => {
        const { methods, totalAmount: amount, change } = paymentData;

        // Если есть клиент, добавляем customer_id
        if (customer) {
            paymentData.customer_id = customer.id;
        }

        // Формируем сообщение об оплате
        const methodsText = methods.map(m => m.name).join(', ');

        // Очищаем корзину и клиента после успешной оплаты
        clearCart();
        setPaymentModalVisible(false);

        // Включаем обратно перехват штрихкодов
        setIsBarcodeListenerActive(true);
    }, [messageApi, clearCart, customer]);

    // Обработчик отмены оплаты
    const handlePaymentCancel = useCallback(() => {
        setPaymentModalVisible(false);
        // Включаем обратно перехват штрихкодов
        setIsBarcodeListenerActive(true);
    }, []);

    // Обработчик ответа "Да" - клиент копит баллы
    // const handleLoyaltyYes = useCallback(() => {
    //     setLoyaltyPointsModalVisible(false);
    //     setIsBarcodeListenerActive(true);
    //     // Не переходим к оплате
    // }, []);

    // Обработчик ответа "Нет" - клиент не копит баллы
    // const handleLoyaltyNo = useCallback(() => {
    //     setLoyaltyPointsModalVisible(false);
    //     setPaymentModalVisible(true);
    //     // Штрихкоды уже отключены в handlePayment
    // }, []);

    // Непосредственное добавление товара в корзину (внутренняя функция)
    const addToCartInternal = useCallback((product, markCode = null) => {
        setCart(prevCart => {
            if (markCode) {
                // Ищем товар с таким же ID, у которого уже есть mark_code
                const existingMarkedItem = prevCart.find(item =>
                    item.id === product.id && item.mark_code && Array.isArray(item.mark_code)
                );

                if (existingMarkedItem) {
                    // Добавляем маркировку в существующий массив
                    return prevCart.map(item =>
                        item === existingMarkedItem
                            ? { ...item, mark_code: [...item.mark_code, markCode], quantity: item.quantity + 1 }
                            : item
                    );
                } else {
                    // Создаем новую позицию с маркировкой
                    return [...prevCart, { ...product, quantity: 1, mark_code: [markCode] }];
                }
            } else {
                // Обычное добавление без маркировки
                const existingItem = prevCart.find(item => item.id === product.id && !item.mark_code);

                if (existingItem) {
                    return prevCart.map(item =>
                        item.id === product.id && !item.mark_code
                            ? { ...item, quantity: item.quantity + 1 }
                            : item
                    );
                } else {
                    return [...prevCart, { ...product, quantity: 1 }];
                }
            }
        });
    }, []);

    // Обработчик подтверждения возраста
    const handleAgeVerified = useCallback(() => {
        setAgeVerified(true);
        setAgeVerificationModalVisible(false);
        setIsBarcodeListenerActive(true);

        // Добавляем товар, для которого требовалась проверка
        if (pendingAdultProduct) {
            addToCartInternal(pendingAdultProduct, pendingAdultMarkCode);
            setPendingAdultProduct(null);
            setPendingAdultMarkCode(null);
        }
    }, [pendingAdultProduct, pendingAdultMarkCode, addToCartInternal]);

    // Обработчик отмены проверки возраста
    const handleAgeVerificationCancel = useCallback(() => {
        setAgeVerificationModalVisible(false);
        setIsBarcodeListenerActive(true);
        setPendingAdultProduct(null);
        setPendingAdultMarkCode(null);
        messageApi.info('Товар не добавлен - требуется подтверждение возраста');
    }, [messageApi]);

    // Добавление товара в корзину с проверкой возраста
    const addToCart = useCallback((product, markCode = null) => {
        // Проверяем, требуется ли проверка возраста
        if (product.is_adult === 1 && !ageVerified) {
            // Сохраняем товар для добавления после проверки
            setPendingAdultProduct(product);
            setPendingAdultMarkCode(markCode);
            setAgeVerificationModalVisible(true);
            setIsBarcodeListenerActive(false);
            return;
        }

        // Если проверка не требуется или уже пройдена - добавляем
        addToCartInternal(product, markCode);
    }, [ageVerified, addToCartInternal]);

    const addToCartAndClearSearch = useCallback((product) => {
        addToCart(product);
        setSearchQuery('');
        setSearchResults([]);
        // Убираем фокус с инпута поиска
        document.activeElement?.blur();
    }, [addToCart]);

    // Удаление товара из корзины
    const removeFromCart = useCallback((productId) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === productId);

            if (existingItem && existingItem.quantity > 1) {
                // Уменьшаем количество
                return prevCart.map(item =>
                    item.id === productId
                        ? { ...item, quantity: item.quantity - 1 }
                        : item
                );
            } else {
                // Удаляем товар полностью
                return prevCart.filter(item => item.id !== productId);
            }
        });
    }, []);

    // Полное удаление товара из корзины
    const removeItemCompletely = useCallback((productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
        messageApi.info('Товар удален из корзины');
    }, [messageApi]);


    // Поиск товаров
    const handleSearch = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        try {
            const results = await searchProducts(query.trim(), 'auto', 7);
            setSearchResults(results);
        } catch (error) {
            console.error('Ошибка поиска товаров:', error);
            setSearchResults([]);
        }
    }, []);

    // Обработка очереди штрихкодов
    const processBarcodeQueue = useCallback(async () => {
        if (isProcessingBarcode || barcodeQueue.length === 0) return;

        setIsProcessingBarcode(true);

        try {
            const code = barcodeQueue[0];
            setBarcodeQueue(prev => prev.slice(1));

            // Проверка на карту клиента
            if (code.length === 16 && code.startsWith('1160')) {
                try {
                    const customerResponse = await deviceSalesService.getCostumerByCardNumber(code);
                    if (customerResponse.status === 'error') {
                        messageApi.error('Карта клиента не найдена');
                    } else {
                        setCustomer({
                            id: customerResponse.data.id,
                            full_name: customerResponse.data.full_name,
                            balance: customerResponse.data.balance
                        });
                        messageApi.success(`Карта клиента активирована`);
                    }
                } catch (error) {
                    console.error('Ошибка получения данных клиента:', error);
                    messageApi.error('Ошибка при получении данных клиента');
                }
            }

            // Проверка на код маркировки
            else if (isMarkingCode(code)) {
                const parsed = parseMarkingCode(code);
                if (parsed) {
                    // Проверка на дубликат
                    const isDuplicate = cart.some(item =>
                        item.mark_code &&
                        (Array.isArray(item.mark_code)
                                ? item.mark_code.includes(parsed.markCode)
                                : item.mark_code === parsed.markCode
                        )
                    );

                    if (isDuplicate) {
                        setDuplicateMarkingCode(parsed.markCode);
                        setDuplicateMarkingModalVisible(true);
                        return;
                    }

                    const product = await findProductByBarcode(parsed.gtin);
                    if (product) {
                        // Проверка что товар требует маркировку
                        if (product.is_marking !== 1) {
                            messageApi.warning('Этот товар не требует маркировки');
                            return;
                        }
                        addToCart(product, parsed.markCode);
                    } else {
                        setProductNotFoundBarcode(parsed.gtin);
                        setProductNotFoundModalVisible(true);
                        setIsBarcodeListenerActive(false);
                    }
                }
            }

            // Обычная обработка штрихкода
            else {
                const product = await findProductByBarcode(code);
                if (product) {
                    // Проверяем, требуется ли маркировка
                    if (product.is_marking === 1) {
                        setPendingMarkingProduct(product);
                        setMarkingModalVisible(true);
                        setIsBarcodeListenerActive(false);
                    } else {
                        addToCart(product);
                    }
                } else {
                    setProductNotFoundBarcode(code);
                    setProductNotFoundModalVisible(true);
                    setIsBarcodeListenerActive(false);
                }
            }
        } catch (error) {
            console.error('Ошибка обработки штрихкода:', error);
            messageApi.error('Ошибка при поиске товара по штрихкоду');
        } finally {
            setIsProcessingBarcode(false);
        }
    }, [isProcessingBarcode, barcodeQueue, addToCart, messageApi]);


    // Обработчик ввода штрихкода
    const handleBarcodeSubmit = useCallback(() => {
        if (!barcodeQuery.trim()) return;

        setBarcodeQueue(prev => [...prev, barcodeQuery.trim()]);
        setBarcodeQuery('');
    }, [barcodeQuery]);

    // Обработчик перехвата штрихкода сканером
    const handleBarcodeScanned = useCallback((result) => {
        if (!isBarcodeListenerActive) return;

        setBarcodeQueue(prev => [...prev, result]);
    }, [isBarcodeListenerActive, messageApi]);

    // Обработчик перехвата ввода сканера штрихкодов
    const handleKeyPress = useCallback((event) => {
        if (!isBarcodeListenerActive) return;

        // Игнорируем если фокус на инпуте
        if (document.activeElement && document.activeElement.tagName === 'INPUT') return;

        const currentTime = Date.now();
        const timeDiff = currentTime - lastKeystroke;

        // Если между нажатиями больше 200мс - это новый штрихкод
        if (timeDiff > 200) {
            setBarcodeBuffer('');
        }

        setLastKeystroke(currentTime);

        if (event.key === 'Enter') {
            // Завершение штрихкода
            if (barcodeBuffer.length >= 4) {
                setBarcodeQueue(prev => [...prev, barcodeBuffer]);
            }
            setBarcodeBuffer('');
            event.preventDefault();
        } else if (event.key.length === 1 && /[0-9a-zA-Z]/.test(event.key)) {
            // Добавляем только цифры и буквы
            setBarcodeBuffer(prev => prev + event.key);
            event.preventDefault();
        }
    }, [isBarcodeListenerActive, barcodeBuffer, lastKeystroke, messageApi]);

    // Обработчик изменения поиска
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchQuery(value);
        handleSearch(value);
    }, [handleSearch]);

    // Обработчик сохранения изменений в модале
    const handleModalSave = useCallback(() => {
        const value = parseFloat(modalValue) || 0;

        if (value === 0) {
            removeItemCompletely(selectedCartItem.id);
            setModalVisible(false);
            setSelectedCartItem(null);
            setModalValue('');
            return;
        }

        // Проверка на маркированный товар
        const isMarked = selectedCartItem.is_marking === 1;

        if (modalMode === 'quantity' && isMarked) {
            const currentMarkings = selectedCartItem.mark_code ?
                (Array.isArray(selectedCartItem.mark_code) ? selectedCartItem.mark_code.length : 1) : 0;

            // Запрещаем уменьшение количества
            if (value < currentMarkings) {
                messageApi.warning('Нельзя уменьшить количество маркированного товара. Удалите позицию полностью.');
                return;
            }

            // Если нужно добавить маркировки
            if (value > currentMarkings) {
                const missing = value - currentMarkings;
                setMissingMarkingsCount(missing);
                setPendingMarkingProduct(selectedCartItem);
                setModalVisible(false);
                setMarkingModalVisible(true);
                setIsBarcodeListenerActive(false);
                return;
            }
        }

        // Обычное изменение
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.id === selectedCartItem.id) {
                    if (modalMode === 'quantity') {
                        return { ...item, quantity: value };
                    } else {
                        return { ...item, price: value };
                    }
                }
                return item;
            });
        });

        setModalVisible(false);
        setSelectedCartItem(null);
        setModalValue('');
    }, [modalValue, modalMode, selectedCartItem, removeItemCompletely, messageApi]);

    const handleMarkingSubmit = useCallback((markCode) => {
        if (!pendingMarkingProduct) return;

        // Проверка требования маркировки
        if (pendingMarkingProduct.is_marking !== 1) {
            messageApi.error('Этот товар не требует маркировки');
            return;
        }

        if (!isMarkingCode(markCode)) {
            messageApi.error('Некорректный код маркировки');
            return;
        }

        const parsed = parseMarkingCode(markCode);
        if (!parsed) {
            messageApi.error('Не удалось распознать код маркировки');
            return;
        }

        if (pendingMarkingProduct.barcode && !validateMarkingForProduct(markCode, pendingMarkingProduct.barcode)) {
            messageApi.error('Код маркировки не соответствует товару');
            return;
        }

        const isDuplicate = cart.some(item =>
            item.mark_code &&
            (Array.isArray(item.mark_code)
                    ? item.mark_code.includes(parsed.markCode)
                    : item.mark_code === parsed.markCode
            )
        );

        if (isDuplicate) {
            setDuplicateMarkingCode(parsed.markCode);
            setDuplicateMarkingModalVisible(true);
            return;
        }

        addToCart(pendingMarkingProduct, parsed.markCode);

        // Проверяем, нужно ли еще маркировки
        if (missingMarkingsCount > 1) {
            setMissingMarkingsCount(prev => prev - 1);
            messageApi.success(`Осталось отсканировать: ${missingMarkingsCount - 1}`);
        } else {
            messageApi.success('Маркированный товар добавлен');
            setMarkingModalVisible(false);
            setPendingMarkingProduct(null);
            setMissingMarkingsCount(0);
            setIsBarcodeListenerActive(true);
        }
    }, [pendingMarkingProduct, addToCart, messageApi, cart, missingMarkingsCount]);

    const handleMarkingCancel = useCallback(() => {
        messageApi.info('Товар не добавлен - требуется код маркировки');
        setMarkingModalVisible(false);
        setPendingMarkingProduct(null);
        setIsBarcodeListenerActive(true);
    }, [messageApi]);

    const handleProductNotFoundClose = useCallback(() => {
        setProductNotFoundModalVisible(false);
        setProductNotFoundBarcode('');
        setIsBarcodeListenerActive(true);
    }, []);

    // Функции для работы с корзиной в localStorage
    const getCartStorageKey = useCallback(() => {
        try {
            const deviceInfo = JSON.parse(localStorage.getItem('device_register_info') || '{}');
            const cashRegisterId = deviceInfo.cash_register?.id;
            return cashRegisterId ? `sales_cart_${cashRegisterId}` : 'sales_cart_default';
        } catch (error) {
            console.error('Ошибка получения ключа корзины:', error);
            return 'sales_cart_default';
        }
    }, []);

    const saveCartToStorage = useCallback((cartData) => {
        try {
            if (cartData.length === 0) {
                localStorage.removeItem('sales_cart');
            } else {
                // Получаем текущее значение pay_card или устанавливаем false
                const currentData = loadCartFromStorage();
                const cartStorage = {
                    items: cartData,
                    pay_card: currentData.pay_card || false
                };
                localStorage.setItem('sales_cart', JSON.stringify(cartStorage));
            }
        } catch (error) {
            console.error('Ошибка сохранения корзины:', error);
        }
    }, []);

    const loadCartFromStorage = useCallback(() => {
        try {
            const savedCart = localStorage.getItem('sales_cart');
            if (savedCart) {
                const parsed = JSON.parse(savedCart);
                return {
                    items: parsed.items || [],
                    pay_card: parsed.pay_card || false
                };
            }
            return { items: [], pay_card: false };
        } catch (error) {
            console.error('Ошибка загрузки корзины:', error);
            return { items: [], pay_card: false };
        }
    }, []);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Режим продаж');

        setRightContent(
            <Space>
                <Button
                    icon={<ClearOutlined />}
                    onClick={clearCart}
                    disabled={cart.length === 0 && !customer}
                >
                    Очистить корзину
                </Button>
            </Space>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, cart.length, customer, clearCart]);

    // useEffect для загрузки данных и обработки очереди:
    useEffect(() => {
        // Загружаем сохраненную корзину
        const savedData = loadCartFromStorage();
        if (savedData.items.length > 0) {
            setCart(savedData.items);
        }
    }, [loadCartFromStorage]);

    useEffect(() => {
        if (barcodeQueue.length > 0 && !isProcessingBarcode) {
            processBarcodeQueue();
        }
    }, [barcodeQueue, isProcessingBarcode, processBarcodeQueue]);

    // Сохранение корзины при изменении
    useEffect(() => {
        saveCartToStorage(cart);
    }, [cart, saveCartToStorage]);


    // Таймер для модала баллов лояльности
    // useEffect(() => {
    //     if (loyaltyPointsModalVisible && loyaltyPointsTimer > 0) {
    //         const timer = setTimeout(() => {
    //             setLoyaltyPointsTimer(prev => prev - 1);
    //         }, 1000);
    //
    //         return () => clearTimeout(timer);
    //     }
    // }, [loyaltyPointsModalVisible, loyaltyPointsTimer]);

    // Подписка на события клавиатуры для перехвата штрихкодов
    useEffect(() => {
        if (isBarcodeListenerActive) {
            const handleKeyDown = (e) => {
                if (!isBarcodeListenerActive) return;

                // Игнорируем если фокус на инпуте
                if (document.activeElement && document.activeElement.tagName === 'INPUT') return;

                const currentTime = Date.now();
                const timeDiff = currentTime - lastKeystroke;

                // Если между нажатиями больше 200мс - это новый штрихкод
                if (timeDiff > 200) {
                    setBarcodeBuffer('');
                }

                setLastKeystroke(currentTime);

                // Обработка суффиксов завершения: Enter (13), LF (10)
                if (e.keyCode === 13 || e.keyCode === 10 || e.key === 'Enter') {
                    if (barcodeBuffer.length >= 4) {
                        setBarcodeQueue(prev => [...prev, barcodeBuffer]);
                    }
                    setBarcodeBuffer('');
                    e.preventDefault();
                } else if (e.key.length === 1) {
                    // Добавляем только цифры и буквы
                    setBarcodeBuffer(prev => prev + e.key);
                    e.preventDefault();
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            return () => {
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        }
    }, [isBarcodeListenerActive, barcodeBuffer, lastKeystroke, messageApi]);

    // Подсчет общей суммы и количества товаров
    const getTotalQuantity = () => {
        return cart.reduce((sum, item) => sum + item.quantity, 0);
    };

    const getTotalAmount = () => {
        return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    // Форматирование текста количества товаров
    const getQuantityText = (quantity) => {
        if (quantity === 0) return '0 товаров';
        if (quantity === 1) return '1 товар';
        if (quantity >= 2 && quantity <= 4) return `${quantity} товара`;
        return `${quantity} товаров`;
    };

    // Обработчик клика по товару в корзине
    const handleCartItemClick = useCallback((item) => {
        setSelectedCartItem(item);
        setModalMode('quantity');
        setModalValue(item.quantity.toString());
        setModalVisible(true);
    }, []);

    // Обработчик изменения режима в модале
    const handleModeChange = useCallback((mode) => {
        setModalMode(mode);
        if (mode === 'quantity') {
            setModalValue(selectedCartItem.quantity.toString());
        } else {
            // Показываем цену за единицу товара
            setModalValue(selectedCartItem.price.toString());
        }
    }, [selectedCartItem]);

    // Обработчик клавиатуры в модале
    const handleKeypadPress = useCallback((value) => {
        if (value === 'backspace') {
            setModalValue(prev => prev.slice(0, -1));
        } else if (value === 'clear') {
            setModalValue('');
        } else {
            setModalValue(prev => prev + value);
        }
    }, []);

    // Обработчик изменения значения кнопками +/-
    const handleValueChange = useCallback((increment) => {
        const currentValue = parseInt(modalValue) || 0;
        const newValue = Math.max(0, currentValue + increment);
        setModalValue(newValue.toString());
    }, [modalValue]);

    // Обработчик закрытия модала
    const handleModalCancel = useCallback(() => {
        setModalVisible(false);
        setSelectedCartItem(null);
        setModalValue('');
    }, []);

    const isElectron = Boolean(window?.process?.versions?.electron);


    return (
        <div style={{
            padding: '0 0px',
            height: 'calc(100vh - 45px)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            overflow: 'hidden'
        }}>
            <Row gutter={1} style={{ height: '100%', margin: 0 }}>
                {/* Левый блок - Быстрые товары */}
                <Col span={14} style={{ height: '100%' }}>
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

                        {/* Ввод штрихкода для теста*/}
                        {!isElectron && (
                            <Input
                            placeholder="Сканировать штрихкод или ввести вручную..."
                            value={barcodeQuery}
                        onChange={(e) => setBarcodeQuery(e.target.value)}
                        onPressEnter={handleBarcodeSubmit}
                        style={{ marginBottom: 16 }}
                        suffix={
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {barcodeQueue.length > 0 && `В очереди: ${barcodeQueue.length}`}
                            </Text>
                        }
                    />
                        )}

                        {/* Компонент быстрых товаров */}
                        <div style={{ flex: 1 }}>
                            <QuickProducts
                                onAddToCart={addToCart}
                                searchQuery={searchQuery}
                                onSearchChange={handleSearchChange}
                                searchResults={searchResults}
                                onAddToCartAndClearSearch={addToCartAndClearSearch}
                            />
                        </div>
                    </div>
                </Col>

                {/* Правый блок - Корзина */}
                <Col span={10} style={{height: '100%'}}>
                    <Card
                        bordered={false}
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        bodyStyle={{
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        {/* Заголовок с кнопкой очистки */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                            <div>
                                <Text style={{ fontSize: '20px', fontWeight: 600, lineHeight: '24px' }}>
                                    Продажа
                                </Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text type="secondary" style={{ fontSize: '16px', fontWeight: 600, lineHeight: '19px' }}>
                                        {getQuantityText(getTotalQuantity())}
                                    </Text>
                                </div>
                            </div>

                            <Button
                                type="text"
                                icon={
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M14.8649 4.65027H11.5248V4.01025C11.5248 2.62025 10.3949 1.49023 9.00488 1.49023C7.61488 1.49023 6.48486 2.62025 6.48486 4.01025V4.65027H3.14484C2.76484 4.65027 2.46484 4.96026 2.46484 5.33026C2.46484 5.70026 2.77484 6.01025 3.14484 6.01025H4.09485V14.0602C4.09485 15.4202 5.19486 16.5103 6.54486 16.5103H11.4548C12.8148 16.5103 13.9048 15.4103 13.9048 13.7103V6.01025H14.8549C15.2349 6.01025 15.5349 5.70026 15.5349 5.33026C15.5349 4.96026 15.2249 4.65027 14.8549 4.65027H14.8649ZM7.83484 4.01025C7.83484 3.37025 8.35487 2.85028 8.99487 2.85028C9.63487 2.85028 10.1548 3.37025 10.1548 4.01025V4.65027H7.83484V4.01025ZM8.07483 13.2402C8.07483 13.7502 7.66485 14.1603 7.15485 14.1603C6.64485 14.1603 6.23486 13.7502 6.23486 13.2402V8.33026C6.23486 7.82026 6.64485 7.41028 7.15485 7.41028C7.66485 7.41028 8.07483 7.82026 8.07483 8.33026V13.2402ZM11.7549 13.2402C11.7549 13.7502 11.3448 14.1603 10.8348 14.1603C10.3248 14.1603 9.91486 13.7502 9.91486 13.2402V8.33026C9.91486 7.82026 10.3248 7.41028 10.8348 7.41028C11.3448 7.41028 11.7549 7.82026 11.7549 8.33026V13.2402Z" fill="#F54D3D"/>
                                    </svg>
                                }
                                onClick={clearCart}
                                disabled={cart.length === 0 && !customer}
                                style={{
                                    color: '#F54D3D',
                                    padding: '4px 8px',
                                    height: 'auto'
                                }}
                            />
                        </div>

                        {/* Блок клиента */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 0',
                            marginBottom: 8
                        }}>
                            <div style={{
                                width: 38,
                                height: 38,
                                borderRadius: '50%',
                                backgroundColor: 'var(--ant-color-fill-tertiary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 12
                            }}>
                                <IconUserFilled/>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Text style={{ fontSize: '16px', fontWeight: 600, lineHeight: '19px' }}>
                                    {customer ? customer.full_name : 'Отсканируйте карту клиента'}
                                </Text>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {customer ? `${customer.balance.toLocaleString()} Б` : ''}
                                </Text>
                            </div>
                        </div>

                        <Divider style={{ margin: '4px 0 8px 0' }} />

                        {/* Список товаров в корзине */}
                        <div style={{flex: 1, overflow: 'auto', marginBottom: 16}}>
                            {cart.length === 0 ? (
                                <div>
                                </div>
                            ) : (
                                <List
                                    dataSource={[...cart].reverse()}
                                    renderItem={(item) => (
                                        <List.Item
                                            style={{
                                                padding: '12px 0',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleCartItemClick(item)}
                                        >
                                            <div style={{width: '100%'}}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                }}>
                                                    <Text strong style={{fontSize: '16px'}}>
                                                        {item.name}
                                                    </Text>
                                                    <Space direction="vertical" size="small" style={{textAlign: 'right', width: '250px'}}>
                                                        <Text strong style={{fontSize: '16px'}}>
                                                            {(item.price * item.quantity).toLocaleString()} ₸
                                                        </Text>
                                                        <Text type="secondary" style={{fontSize: '14px'}}>
                                                            {item.price.toLocaleString()} ₸ × {item.quantity}
                                                        </Text>
                                                    </Space>

                                                </div>

                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </div>

                        {/* Кнопка оплаты */}
                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={handlePayment}
                            disabled={cart.length === 0}
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                        >
                            К оплате {getTotalAmount().toLocaleString()} ₸
                        </Button>
                    </Card>
                </Col>
            </Row>

            {/* Модальное окно для изменения количества/цены */}
            <Modal
                title={null}
                open={modalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={296}
                centered
            >
                {selectedCartItem && (
                    <div style={{textAlign: 'center'}}>
                        {/* Название товара */}
                        <div style={{textAlign: 'center', fontSize: '16px', fontWeight: 600, lineHeight: '19px', marginBottom: 24}}>
                            {selectedCartItem.name}
                        </div>

                        <Segmented
                            options={[
                                {label: 'Количество', value: 'quantity'},
                                // Опция "Цена" только для админа и старшего кассира
                                ...(deviceCashierRoleService.hasAnyRole(['director_store','senior_cashier'])
                                    ? [{label: 'Цена', value: 'price'}]
                                    : [])
                            ]}
                            value={modalMode}
                            onChange={handleModeChange}
                            style={{marginBottom: 24}}
                            block
                        />

                        {/* Значение с кнопками +/- */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 24,
                            gap: 16
                        }}>
                            <Button
                                icon={<MinusOutlined/>}
                                size="large"
                                onClick={() => handleValueChange(-1)}
                                style={{height: 60, width: 60}}
                            />
                            <div style={{
                                fontSize: '32px',
                                fontWeight: 'bold',
                                minWidth: '110px',
                                textAlign: 'center'
                            }}>
                                {modalValue || '0'}
                            </div>
                            <Button
                                icon={<PlusOutlined/>}
                                size="large"
                                onClick={() => handleValueChange(1)}
                                style={{height: 60, width: 60}}
                            />
                        </div>

                        {/* Клавиатура */}
                        <NumbersKeypad onKeyPress={handleKeypadPress}/>

                        {/* Кнопка готово */}
                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={handleModalSave}
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600',
                                marginTop: 24
                            }}
                        >
                            Готово
                        </Button>
                    </div>
                )}
            </Modal>
            {/* Модальное окно Честный знак */}
            <MarkingCodeModal
                visible={markingModalVisible}
                onCancel={handleMarkingCancel}
                onSubmit={handleMarkingSubmit}
                productName={pendingMarkingProduct?.name}
                productBarcode={pendingMarkingProduct?.barcode}
            />

            {/* Модальное окно проверки возраста */}
            <AgeVerificationModal
                visible={ageVerificationModalVisible}
                onCancel={handleAgeVerificationCancel}
                onVerified={handleAgeVerified}
            />

            {/* Модальное окно дублирующей маркировки */}
            <Modal
                open={duplicateMarkingModalVisible}
                onCancel={() => setDuplicateMarkingModalVisible(false)}
                footer={[
                    <Button
                        key="ok"
                        type="primary"
                        size="large"
                        onClick={() => setDuplicateMarkingModalVisible(false)}
                        style={{
                            height: '70px',
                            fontSize: '18px',
                            lineHeight: '23px',
                            letterSpacing: '-0.44px',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        Понятно
                    </Button>
                ]}
                width={400}
                centered
            >
                <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
            }}>

                <Title level={3} style={{marginBottom: 16}}>
                    Товар с такой маркировкой уже добавлен в чек
                </Title>
                <div style={{textAlign: 'center', marginBottom: 12}}>
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
                         style={{marginBottom: 16}}>
                        <circle cx="32" cy="32" r="32" fill="#FFF3E0"/>
                        <path d="M32 20v16M32 44h.02" stroke="#FF9800" strokeWidth="4" strokeLinecap="round"
                              strokeLinejoin="round"/>
                    </svg>
                </div>

                <Text style={{marginBottom: 12, fontSize: '16px'}}>
                    Похоже, вы перепутали товар. Удалите его и отсканируйте заново.
                </Text>

                </div>
            </Modal>

            {/* Модальное окно вопроса про баллы лояльности */}
            {/*
            <Modal
                open={loyaltyPointsModalVisible}
                onCancel={null}
                footer={null}
                width={400}
                centered
                closable={false}
                maskClosable={false}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                }}>
                    <Title level={3} style={{ marginBottom: 32, marginTop: 8 }}>
                        Спросите: Баллы копите?
                    </Title>

                    Таймер
                    {loyaltyPointsTimer > 0 && (
                        <Text style={{ marginBottom: 24, fontSize: '14px', color: '#8c8c8c' }}>
                            Через {loyaltyPointsTimer} сек.
                        </Text>
                    )}

                    Кнопки
                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <Button
                            size="large"
                            onClick={handleLoyaltyNo}
                            disabled={loyaltyPointsTimer > 0}
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44px',
                                fontWeight: '600',
                                flex: 1
                            }}
                        >
                            Нет
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleLoyaltyYes}
                            disabled={loyaltyPointsTimer > 0}
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44px',
                                fontWeight: '600',
                                flex: 1
                            }}
                        >
                            Да
                        </Button>
                    </div>
                </div>
            </Modal>
            */}

            {/* Модальное окно оплаты */}

            <SalePaymentModal
                visible={paymentModalVisible}
                onCancel={handlePaymentCancel}
                onComplete={handlePaymentComplete}
                totalAmount={getTotalAmount()}
                cart={cart}
                customer={customer}
            />

            {/* Модал товар не найден */}
            <Modal
                open={productNotFoundModalVisible}
                onCancel={handleProductNotFoundClose}
                footer={null}
                width={400}
                centered
                closable={false}
                maskClosable={false}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                }}>
                    <Title level={3} style={{ marginBottom: 16, marginTop: 8 }}>
                        Товар не найден
                    </Title>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="#FFF1F0"/>
                            <path d="M32 20v16M32 44h.02" stroke="#FF4D4F" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <Text style={{ marginBottom: 24, fontSize: '16px' }}>
                        Товар со штрихкодом <strong>{productNotFoundBarcode.replace(/\x1D/g, '[GS]')}</strong> не найден
                    </Text>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleProductNotFoundClose}
                        style={{
                            height: '70px',
                            fontSize: '18px',
                            lineHeight: '23px',
                            letterSpacing: '-0.44px',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        Понятно
                    </Button>
                </div>
            </Modal>

        </div>
    );
};

export default SalesModePage;